import { Ban, CheckCircle2, FileCheck2, History, Landmark, Settings, Undo2, Wallet } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type ComponentType } from 'react';
import { useParams } from 'react-router-dom';
import { usePermission } from '../../../../auth/AuthContext';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { ErrorState } from '../../../../components/ErrorState';
import { Menu } from '../../../../components/Menu';
import { NextActionAlert } from '../../../../components/NextActionAlert';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly, formatDateTime, toIso } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { DisbursementRequestModal } from '../../../cash-division/components/DisbursementRequestModal';
import {
  DisbursementItemsPanel,
  DisbursementLifecycleCard,
  disbursementTabs,
  type DisbursementTabKey,
} from '../../../cash-division/components/DisbursementRequestTabs';
import { DR_STATUS, type DisbursementRequestDetail } from '../../../cash-division/disbursementRequestTypes';
import { myDisbursementRequestsApi, type CashRequester } from '../myDisbursementRequestsApi';
import { selfServiceSource } from '../selfServiceSource';


/**
 * One of the employee's own requests to the Cash Division, followed through the route.
 *
 * <b>It is the Cash Division's profile with every step taken OFF</b>: the banner says where the
 * request is and what happens next, and offers nothing — passing it to Accounting, recording the
 * voucher, releasing and returning are all the Cash Division's acts, on its own keys and its own
 * page. The one write here is correcting the request, while it is still waiting on Cash.
 */
export function MyDisbursementRequestProfilePage() {
  const { id } = useParams<{ id: string }>();

  const canReadHistory = usePermission('audit-logs.read');
  const [request, setRequest] = useState<DisbursementRequestDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [requester, setRequester] = useState<CashRequester | null>(null);
  const [tab, setTab] = useState<DisbursementTabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      myDisbursementRequestsApi
        .get(id, signal)
        .then((next) => {
          if (!signal?.aborted) {
            setRequest(next);
            setError(null);
          }
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this request.', 0));
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  useEffect(() => {
    const controller = new AbortController();
    myDisbursementRequestsApi.me(controller.signal).then(setRequester).catch(() => undefined);

    return () => controller.abort();
  }, []);

  const source = useMemo(() => selfServiceSource(requester), [requester]);

  const breadcrumb: BreadcrumbSegment[] = [
    'Self Service',
    'Cash Division',
    { label: 'Requests', to: '/self-service/disbursement-requests' },
    request ? request.requestNumber : 'Request',
  ];

  // Guarded on having NOTHING to show — a failed refresh keeps the record and goes to the banner.
  if (error && !request) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const record = request;
  const banner = bannerFor(record);
  /*
    A request Cash raised for SOMEBODY ELSE is readable here (the account raised it) but not
    correctable — the server refuses, so the Edit says so before the reader fills the form in.
  */
  /*
    A Self Service correction needs the request to be FOR one of the account's own employee
    records — every one of them, inactive included, which is the set the server checks. Settled
    only once `/me` has answered, so the Edit is never disabled on a guess.
  */
  const ownRecords = requester?.personnelIds ?? [];
  const correctionRefusal =
    requester == null
      ? null
      : ownRecords.length === 0
        ? 'This account is not linked to an employee record, so it cannot correct requests here.'
        : !ownRecords.includes(record.requesterPersonnelId)
          ? 'This request was raised for somebody else. Ask the Cash Division to correct it.'
          : null;
  const editDisabledReason =
    correctionRefusal ??
    (record.isEditable
      ? null
      : !record.isActive
        ? 'The Cash Division deleted this request, so it can no longer be corrected.'
        : 'The Cash Division has already taken this request forward, so it can no longer be corrected.');

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="my-disbursement-request-profile"
        header={
          <ProfileHeader
            id="my-disbursement-request-profile-header"
            icon={<Wallet className="h-6 w-6" aria-hidden="true" />}
            title={record.requestNumber}
            meta={[record.transactionTypeName, record.paymentTypeName, formatMoney(record.amount)]}
            status={
              <span className="flex items-center gap-2">
                <StatusText tone="inactive">{record.statusLabel}</StatusText>
                <StatusText tone={record.isActive ? 'active' : 'inactive'}>
                  {record.isActive ? 'Active' : 'Deleted'}
                </StatusText>
              </span>
            }
            createdBy={record.createdByName}
            createdAt={record.createdAt}
            // NO COG for a reader without the one entry in it — `<Menu>` draws its trigger regardless.
            actions={
              canReadHistory ? (
              <Menu
                id="my-disbursement-request-profile-settings"
                items={[
                  {
                    key: 'history',
                    label: 'History Logs',
                    icon: <History className="h-4 w-4" aria-hidden="true" />,
                    permission: 'audit-logs.read',
                    onClick: () => setHistoryOpen(true),
                  },
                ]}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Request settings"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
              ) : null
            }
          />
        }
        banner={
          <NextActionAlert
            error={Boolean(error)}
            id="my-disbursement-request-banner"
            icon={<banner.icon className="h-4 w-4" aria-hidden="true" />}
            title={error ? 'This page may be out of date' : banner.title}
            description={error ? error.message : banner.description}
          />
        }
        // The Items tab owns its own chrome and runs edge to edge — the purchase request's layout.
        panel={tab === 'items' ? 'bleed' : 'cards'}
        tabs={disbursementTabs(record)}
        activeTab={tab}
        onTabChange={(next) => setTab(next as DisbursementTabKey)}
      >
        {tab === 'items' ? (
          <DisbursementItemsPanel id="my-disbursement-request-profile-items" record={record} />
        ) : tab === 'lifecycle' ? (
          /*
            Received by is not named here: resolving a name needs `personnel.read`, which this
            surface exists not to hand out. The event still says which account recorded it.
          */
          <DisbursementLifecycleCard id="my-disbursement-request-profile-lifecycle" record={record} receivedByName={null} />
        ) : (
          <>
            <ProfileCard
              id="my-disbursement-request-profile-request"
              title="Request"
              onEdit={() => setEditing(true)}
              editPermission="my-disbursement-requests.update"
              editDisabledReason={editDisabledReason}
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Request number', value: record.requestNumber, mono: true },
                  { label: 'Transaction type', value: record.transactionTypeName },
                  { label: 'Payment type', value: record.paymentTypeName },
                  { label: 'Total amount', value: formatMoney(record.amount), mono: true },
                  { label: 'Remarks', value: record.notes },
                  { label: 'Voucher number', value: record.voucherNumber, mono: true },
                  {
                    label: 'Voucher date',
                    value: record.voucherDate != null ? formatDateOnly(record.voucherDate) : null,
                    mono: true,
                  },
                  { label: 'Payment reference', value: record.disbursementReference, mono: true },
                ]}
              />
            </ProfileCard>

            {/* Audit is never editable: created and updated are a record of what happened. */}
            <ProfileCard id="my-disbursement-request-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: record.createdByName },
                  {
                    label: 'Created',
                    value: (
                      <time dateTime={toIso(record.createdAt)} className="font-mono">
                        {formatDateTime(record.createdAt)}
                      </time>
                    ),
                  },
                  { label: 'Updated by', value: record.updatedByName },
                  {
                    label: 'Updated',
                    value:
                      record.updatedAt != null ? (
                        <time dateTime={toIso(record.updatedAt)} className="font-mono">
                          {formatDateTime(record.updatedAt)}
                        </time>
                      ) : null,
                  },
                ]}
              />
            </ProfileCard>
          </>
        )}
      </ProfileHeaderShell>

      {editing ? (
        <DisbursementRequestModal
          open
          editing={record}
          self={source}
          onClose={() => setEditing(false)}
          onSaved={(next) => {
            setRequest(next);
            setEditing(false);
          }}
        />
      ) : null}

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="my-disbursement-requests"
        entityId={record.id}
      />
    </div>
  );
}

/** One banner for every state, written for the person who asked — they do none of the steps. */
function bannerFor(record: DisbursementRequestDetail): {
  icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' }>;
  title: string;
  description: string;
  
} {
  if (!record.isActive) {
    return {
      icon: Ban,
      title: 'Deleted by the Cash Division',
      description: 'This request was taken off the register. Ask the Cash Division if it should not have been.',
      
    };
  }

  switch (record.status) {
    case DR_STATUS.cashProcessing:
      return {
        icon: Wallet,
        title: 'Waiting on the Cash Division',
        description:
          'The Cash Division will check it and send it for accounting processing. You can still correct it until then.',
        
      };
    case DR_STATUS.atAccounting:
      return {
        icon: Landmark,
        title: 'At Accounting',
        description: 'Accounting is preparing the voucher. The Cash Division records it here when it comes back.',
        
      };
    case DR_STATUS.forRelease:
      return {
        icon: FileCheck2,
        title: 'For release',
        description: `Voucher ${record.voucherNumber ?? ''} is ready. The Cash Division will release the payment.`,
        
      };
    case DR_STATUS.disbursed:
      return {
        icon: CheckCircle2,
        title: 'Disbursed',
        description: `Paid out on ${record.disbursedOn != null ? formatDateOnly(record.disbursedOn) : '—'} under ${record.disbursementReference ?? '—'}.`,
        
      };
    case DR_STATUS.returned:
      return {
        icon: Undo2,
        title: 'Returned',
        description: `The Cash Division returned it${record.returnedFromStatusLabel != null ? ` from ${record.returnedFromStatusLabel}` : ''}: ${record.returnReason ?? ''}`,
        
      };
    default:
      return { icon: Wallet, title: record.statusLabel, description: '' };
  }
}
